import {
  Alert,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  monthOptions,
  ordinalOptions,
  repeatOptions,
  weekLongOptions,
  weekShortOptions,
} from '../../constants';
import dayjs from 'dayjs';
import _ from 'lodash';

const CustomSchedule = ({ open, onClose, onSubmit, initialData }) => {
  const [form] = Form.useForm();
  const [value, setValue] = useState(1);
  const [selected, setSelected] = useState('day');
  const [selectedDays, setSelectedDays] = useState([dayjs()?.format('dddd')]);

  const [repeatDayType, setRepeatDayType] = useState('day');
  const [specificDay, setSpecificDay] = useState(1);
  const [ordinal, setOrdinal] = useState('First');
  const [weekday, setWeekday] = useState('Monday');

  const [selectedMonth, setSelectedMonth] = useState('January');

  const [summery, setSummery] = useState('');

  const resetFields = () => {
    setValue(1);
    setSelectedDays([dayjs()?.format('dddd')]);
    setRepeatDayType('day');
    setSpecificDay(1);
    setOrdinal('First');
    setWeekday('Monday');
    setSelectedMonth('January');
  };

  const getMaxValue = () => {
    switch (selected) {
      case 'week':
        return 52;
      case 'month':
        return 12;
      case 'day':
        return 365;
      default:
    }
  };

  const handleInputChange = (e) => {
    const inputValue = parseInt(e.target.value, 10);
    const max = getMaxValue();
    if (inputValue <= max || selected === 'year') {
      setValue(inputValue);
    } else {
      setValue(max);
    }
  };

  const onWeekDaysChange = (checkedValues) => {
    const weekOrder = weekShortOptions?.map((el) => el.value);
    const sortedDays = _.sortBy(checkedValues, (day) => weekOrder.indexOf(day));
    setSelectedDays(sortedDays);
  };

  // Month Type
  const handleSpecificDayChange = (e) => {
    const day = parseInt(e.target.value, 10);
    setSpecificDay(day);
  };

  const handleRepeatDayTypeChange = (e) => {
    setRepeatDayType(e.target.value);
  };

  const handleOrdinalChange = (value) => {
    setOrdinal(value);
  };

  const handleWeekdayChange = (value) => {
    setWeekday(value);
  };

  // Year Options
  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  // Payload
  const preparePayload = () => {
    const payload = {
      frequency: selected,
      interval: value,
      type: 'custom',
    };

    if (selected === 'week') {
      payload.weekdays = selectedDays;
    }

    if (selected === 'month' || selected === 'year') {
      payload.repeatDayType = repeatDayType;
      if (repeatDayType === 'day') {
        payload.specificDay = specificDay;
      } else if (repeatDayType === 'ordinal') {
        payload.ordinal = ordinal;
        payload.weekday = weekday;
      }
    }

    if (selected === 'year') {
      payload.month = selectedMonth;
    }

    payload.summary = summery;
    return payload;
  };

  const handleSubmit = () => {
    const payload = preparePayload();
    onSubmit(payload);
    onClose();
  };

  useEffect(() => {
    if (initialData && initialData.type === 'custom') {
      setValue(initialData.interval || 1);
      setSelected(initialData.frequency || 'day');
      setSelectedDays(initialData.weekdays || [dayjs()?.format('dddd')]);
      setRepeatDayType(initialData.repeatDayType || 'day');
      setSpecificDay(initialData.specificDay || 1);
      setOrdinal(initialData.ordinal || 'First');
      setWeekday(initialData.weekday || 'Monday');
      setSelectedMonth(initialData.month || 'January');
    }
  }, [initialData]);

  useEffect(() => {
    let summaryText = '';

    switch (selected) {
      case 'day':
        if (value === 1) summaryText = 'Occurs every day.';
        else summaryText = `Occurs every ${value} days.`;
        break;
      case 'week':
        if (selectedDays.length === 1) {
          if (value === 1) summaryText = `Occurs every week on ${selectedDays}.`;
          else summaryText = `Occurs every ${value} weeks on ${selectedDays}.`;
        } else {
          let weekDays =
            selectedDays.length === 2 ? selectedDays.join(' and ') : selectedDays.join(', ');
          summaryText = weekDays && `Occurs every week on ${weekDays}.`;
        }
        break;
      case 'month':
        switch (repeatDayType) {
          case 'day':
            if (value === 1) summaryText = `Occurs every month on day ${specificDay}.`;
            else summaryText = `Occurs every ${value} months on day ${specificDay}.`;
            break;

          case 'ordinal':
            if (value === 1)
              summaryText = `Occurs every month on the ${ordinal.toLowerCase()} ${weekday}.`;
            else
              summaryText = `Occurs every ${value} months on the ${ordinal.toLowerCase()} ${weekday}.`;

            break;
          default:
            break;
        }
        break;

      case 'year':
        switch (repeatDayType) {
          case 'day':
            if (value === 1)
              summaryText = `Occurs every year on day ${specificDay} of ${selectedMonth}.`;
            else
              summaryText = `Occurs every ${value} years on day ${specificDay} of ${selectedMonth}.`;
            break;

          case 'ordinal':
            if (value === 1)
              summaryText = `Occurs every year on the ${ordinal.toLowerCase()} ${weekday} of ${selectedMonth}.`;
            else
              summaryText = `Occurs every ${value} years on the ${ordinal.toLowerCase()} ${weekday} of ${selectedMonth}.`;
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }

    setSummery(summaryText);
  }, [value, selected, selectedDays, repeatDayType, specificDay, ordinal, weekday, selectedMonth]);

  return (
    <Modal
      title="Custom Schedule"
      open={open}
      onCancel={onClose}
      centered
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: '#00000015',
        },
        content: {
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        },
      }}
      wrapClassName="custom-schedule-modal"
      okText="Save"
      onOk={handleSubmit}
    >
      <Form
        form={form}
        initialValues={{
          value: 1,
          selectedDays: [dayjs()?.format('dddd')?.toLowerCase()],
          repeatDayType: 'day',
          specificDay: 1,
          ordinal: 'First',
          weekday: 'Mon',
          selectedMonth: 'January',
        }}
      >
        <Typography.Text>Repeat Every:</Typography.Text>
        <Flex gap={10} style={{ marginBlock: '10px' }}>
          <Input
            type="number"
            defaultValue={1}
            min={1}
            max={getMaxValue()}
            value={value}
            onChange={handleInputChange}
            disabled={selectedDays.length > 1}
            style={{ width: '100px' }}
          />
          <Select
            options={repeatOptions}
            value={selected}
            onChange={(val) => {
              setSelected(val);
              resetFields();
            }}
            style={{ minWidth: '100px' }}
          />
        </Flex>
        {/* Week Schedule Options */}
        {selected === 'week' && (
          <div>
            <Typography.Text>On:</Typography.Text>
            <Flex gap={10} style={{ marginTop: '10px' }}>
              <Checkbox.Group value={selectedDays} onChange={onWeekDaysChange}>
                <Row gutter={[16, 16]} style={{ display: 'flex', justifyContent: 'center' }}>
                  {weekShortOptions.map((option) => (
                    <Col key={option.value}>
                      <Checkbox
                        value={option.value}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: '14px',
                          margin: '5px',
                        }}
                      >
                        {option.label}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Flex>
            {!selectedDays.length && (
              <Typography.Text type="danger" style={{ display: 'block' }}>
                Please select at least one day of the week.
              </Typography.Text>
            )}
          </div>
        )}

        {/* Month Schedule Options */}
        {selected === 'month' && (
          <div>
            <Flex gap={10} style={{ marginTop: '10px' }}>
              <Radio.Group
                value={repeatDayType}
                onChange={handleRepeatDayTypeChange}
                style={{ marginTop: '10px' }}
              >
                <Radio value="day">On day</Radio>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={specificDay}
                  onChange={handleSpecificDayChange}
                  style={{ width: '100px', marginLeft: '10px' }}
                  disabled={repeatDayType !== 'day'}
                />
                <div style={{ marginTop: '10px' }}>
                  <Radio value="ordinal" style={{ marginTop: '10px' }}>
                    On the
                  </Radio>
                  <Select
                    options={ordinalOptions}
                    value={ordinal}
                    onChange={handleOrdinalChange}
                    style={{ width: '100px', marginLeft: '10px' }}
                    disabled={repeatDayType !== 'ordinal'}
                  />
                  <Select
                    options={weekLongOptions}
                    value={weekday}
                    onChange={handleWeekdayChange}
                    style={{ width: '120px', marginLeft: '10px' }}
                    disabled={repeatDayType !== 'ordinal'}
                  />
                </div>
              </Radio.Group>
            </Flex>
          </div>
        )}

        {/* Year Schedule Options */}
        {selected === 'year' && (
          <div>
            <Flex gap={10} style={{ marginTop: '10px' }}>
              <Radio.Group
                value={repeatDayType}
                onChange={handleRepeatDayTypeChange}
                style={{ marginTop: '10px' }}
              >
                <Radio value="day">On</Radio>
                <Select
                  options={monthOptions}
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  style={{ width: '120px', marginLeft: '10px' }}
                  disabled={repeatDayType !== 'day'}
                />
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={specificDay}
                  onChange={handleSpecificDayChange}
                  style={{ width: '100px', marginLeft: '10px' }}
                  disabled={repeatDayType !== 'day'}
                />
                <div style={{ marginTop: '10px' }}>
                  <Radio value="ordinal" style={{ marginTop: '10px' }}>
                    On the
                  </Radio>
                  <Select
                    options={ordinalOptions}
                    value={ordinal}
                    onChange={handleOrdinalChange}
                    style={{ width: '100px', marginLeft: '10px' }}
                    disabled={repeatDayType !== 'ordinal'}
                  />
                  <Select
                    options={weekLongOptions}
                    value={weekday}
                    onChange={handleWeekdayChange}
                    style={{ width: '120px', marginLeft: '10px' }}
                    disabled={repeatDayType !== 'ordinal'}
                  />
                  <Typography.Text> of </Typography.Text>
                  <Select
                    options={monthOptions}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    style={{ width: '120px' }}
                    disabled={repeatDayType !== 'ordinal'}
                  />
                </div>
              </Radio.Group>
            </Flex>
          </div>
        )}
      </Form>
      <Divider />
      {summery && <Alert message={summery} type="info" />}
      <Divider />
    </Modal>
  );
};

export default CustomSchedule;
